/**
 * The frozen document and the audit events of one dispatch (batch 8 T12c1, #3033). Cost-bearing and supplier-facing, so it needs
 * `purchase_orders.dispatch`; the read permission alone never shows it. Events are paged by cursor, bounded, and a failed read says so.
 */

import {
  Alert,
  Box,
  Button,
  Divider,
  List,
  ListItem,
  ListItemText,
  Stack,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useDispatchHistory } from '@/hooks/useProcurementDispatch';
import { DispatchDocumentView } from './DispatchDocumentView';
import { Freshness, LoadingBlock, ReadError } from './ReadFeedback';
import { describeDispatchError } from './dispatchErrors';
import { formatDateTimeZone } from './dispatchFormat';
import { HISTORY_PAGE_SIZE, MAX_LOADED_PAGES } from './dispatchApi';
import type { DispatchEvent } from './dispatchTypes';

const KNOWN_EVENTS = new Set([
  'queued',
  'claimed',
  'attempt_failed',
  'sent',
  'marked_uncertain',
  'resolved_sent',
  'resolved_not_sent',
  'cancel_requested',
  'cancelled',
  'ack_rejected',
]);

function EventRow({
  event,
  currentUserId,
}: {
  event: DispatchEvent;
  currentUserId: string | null;
}) {
  const { t, i18n } = useTranslation('procurementDispatch');
  const { reason, code, supplierOrderRef } = event.detail as {
    reason?: unknown;
    code?: unknown;
    supplierOrderRef?: unknown;
  };
  const actor =
    event.actorUserId === null
      ? null
      : event.actorUserId === currentUserId
        ? t('actor.you')
        : t('actor.other');
  return (
    <ListItem disableGutters divider alignItems="flex-start">
      <ListItemText
        primary={
          KNOWN_EVENTS.has(event.event)
            ? t(`history.events.${event.event}`)
            : t('history.events.unknown')
        }
        secondary={
          <>
            <Typography component="span" variant="caption" display="block">
              {formatDateTimeZone(event.createdAt, i18n.language)}
              {actor !== null ? ` · ${actor}` : ''}
            </Typography>
            {typeof reason === 'string' && reason !== '' && (
              <Typography
                component="span"
                variant="body2"
                display="block"
                sx={{ overflowWrap: 'anywhere' }}
              >
                {t('history.reason', { reason })}
              </Typography>
            )}
            {typeof supplierOrderRef === 'string' && supplierOrderRef !== '' && (
              <Typography
                component="span"
                variant="body2"
                display="block"
                sx={{ overflowWrap: 'anywhere' }}
              >
                {t('history.supplierRef', { ref: supplierOrderRef })}
              </Typography>
            )}
            {typeof code === 'string' && code !== '' && (
              <Typography
                component="span"
                variant="caption"
                display="block"
                sx={{ fontFamily: 'monospace' }}
              >
                {t('history.code', { code })}
              </Typography>
            )}
          </>
        }
        slotProps={{ secondary: { component: 'div' } }}
      />
    </ListItem>
  );
}

interface DispatchHistoryProps {
  purchaseOrderId: string;
  dispatchId: string;
  currentUserId: string | null;
}

export function DispatchHistory({
  purchaseOrderId,
  dispatchId,
  currentUserId,
}: DispatchHistoryProps) {
  const { t } = useTranslation('procurementDispatch');
  const history = useDispatchHistory(purchaseOrderId, dispatchId);

  if (history.isLoading) return <LoadingBlock label={t('history.loading')} />;
  if (history.isError && history.events.length === 0 && history.content === null)
    return <ReadError error={history.error} onRetry={history.refetch} />;

  const { content } = history;
  return (
    <Stack spacing={2}>
      <Freshness
        updatedAt={history.updatedAt}
        isStale={history.isStale}
        isRefetching={history.isRefetching}
        error={history.error}
        onRefresh={history.refetch}
      />

      {content !== null && (
        <>
          <Typography variant="subtitle2">{t('history.documentTitle')}</Typography>
          <Typography variant="caption" color="text.secondary">
            {t('history.documentFrozen')}
          </Typography>
          <DispatchDocumentView
            snapshot={content.snapshot}
            recipientEmail={content.recipientEmail}
          />
          <Box
            component="dl"
            sx={{
              m: 0,
              display: 'grid',
              gridTemplateColumns: 'max-content 1fr',
              columnGap: 2,
              rowGap: 0.5,
            }}
          >
            {content.supplierOrderRef !== null && (
              <>
                <Typography component="dt" variant="body2" color="text.secondary">
                  {t('history.supplierRefLabel')}
                </Typography>
                <Typography component="dd" variant="body2" sx={{ m: 0, overflowWrap: 'anywhere' }}>
                  {content.supplierOrderRef}
                </Typography>
              </>
            )}
            {content.providerMessageId !== null && (
              <>
                <Typography component="dt" variant="body2" color="text.secondary">
                  {t('history.providerRefLabel')}
                </Typography>
                <Typography component="dd" variant="body2" sx={{ m: 0, overflowWrap: 'anywhere' }}>
                  {content.providerMessageId}
                </Typography>
              </>
            )}
          </Box>
          {content.lastError !== null && content.lastError !== '' && (
            <Alert severity="error" icon={false}>
              <Typography variant="body2" fontWeight={600}>
                {t('history.lastError')}
              </Typography>
              <Typography
                component="pre"
                variant="body2"
                sx={{
                  m: 0,
                  whiteSpace: 'pre-wrap',
                  overflowWrap: 'anywhere',
                  fontFamily: 'inherit',
                }}
              >
                {content.lastError}
              </Typography>
            </Alert>
          )}
          <Divider />
        </>
      )}

      <Typography variant="subtitle2">{t('history.eventsTitle')}</Typography>
      {history.events.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          {t('history.noEvents')}
        </Typography>
      ) : (
        <List dense disablePadding aria-label={t('history.eventsTitle')}>
          {history.events.map((event) => (
            <EventRow key={event.id} event={event} currentUserId={currentUserId} />
          ))}
        </List>
      )}
      {history.isFetchNextPageError && (
        <Alert severity="error" role="alert">
          {describeDispatchError(t, history.error)}
        </Alert>
      )}
      {history.hasNextPage && (
        <Box>
          <Button onClick={history.fetchNextPage} disabled={history.isFetchingNextPage}>
            {history.isFetchingNextPage ? t('history.loadingMore') : t('history.showMore')}
          </Button>
        </Box>
      )}
      {history.overflow && (
        <Alert severity="info">
          {t('history.overflow', { max: MAX_LOADED_PAGES * HISTORY_PAGE_SIZE })}
        </Alert>
      )}
    </Stack>
  );
}
